import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEOHead } from '../components/SEOHead';
import { StructuredData } from '../components/StructuredData';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';
import { RetentionPopup } from '../components/RetentionPopup';
import { 
  Calculator, 
  BookOpen, 
  Mail, 
  CheckSquare, 
  TrendingUp, 
  Sun, 
  PieChart, 
  BarChart3, 
  Wifi, 
  Zap,
  ArrowRight,
  Sparkles,
  Shield,
  Clock,
  Leaf,
  Phone
} from 'lucide-react';

export const HomePage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [showRetentionPopup, setShowRetentionPopup] = useState(!!location.state?.fromProvider);

  const providerCountry = location.state?.country || null;
  const providerVertical = location.state?.vertical || null;

  const isSpanish = i18n.language === 'es';
  const langPrefix = isSpanish ? '/es' : '/fr';
  const country = isSpanish ? 'ES' : 'FR';
  const saveaPhone = getSaveaPhone(country, 'electricity');

  const heroImages = [
    "https://images.unsplash.com/photo-1579621970795-87facc2f976d",
    "https://images.unsplash.com/photo-1474631245212-32dc3c8310c6",
    "https://images.unsplash.com/photo-1658753145551-8f44e5811d21",
    "https://images.unsplash.com/photo-1758691031749-607d43c14f63",
    "https://images.unsplash.com/photo-1441171205449-f600f908a9f3"
  ];

  const tools = [
    { 
      key: 'templates', 
      icon: Mail, 
      path: `${langPrefix}/${isSpanish ? 'modelos-email' : 'modeles-emails'}`,
      color: 'from-amber-500 to-amber-600',
      badge: '16+ modèles'
    },
    { 
      key: 'moving', 
      icon: CheckSquare, 
      path: `${langPrefix}/${isSpanish ? 'mudanza' : 'demenagement'}`,
      color: 'from-indigo-500 to-indigo-600',
      badge: 'Checklist'
    },
    { 
      key: 'tracker', 
      icon: TrendingUp, 
      path: `${langPrefix}/${isSpanish ? 'seguimiento-precios' : 'tracker-prix'}`,
      color: 'from-rose-500 to-rose-600',
      badge: 'Temps réel'
    },
    { 
      key: 'solar', 
      icon: Sun, 
      path: `${langPrefix}/${isSpanish ? 'simulador-solar' : 'simulateur-solaire'}`,
      color: 'from-orange-500 to-orange-600',
      badge: 'Simulateur'
    },
    { 
      key: 'expenses', 
      icon: PieChart, 
      path: `${langPrefix}/${isSpanish ? 'seguimiento-gastos' : 'suivi-depenses'}`,
      color: 'from-teal-500 to-teal-600',
      badge: 'Suivi mensuel'
    },
    { 
      key: 'inflation', 
      icon: BarChart3, 
      path: `${langPrefix}/${isSpanish ? 'inflacion' : 'inflation'}`,
      color: 'from-purple-500 to-purple-600',
      badge: 'Données INSEE'
    },
    { 
      key: 'internet', 
      icon: Wifi, 
      path: `${langPrefix}/${isSpanish ? 'comparador-internet' : 'comparateur-internet'}`,
      color: 'from-cyan-500 to-cyan-600',
      badge: 'Comparateur'
    },
    { 
      key: 'electricity', 
      icon: Zap, 
      path: `${langPrefix}/${isSpanish ? 'luz' : 'comparateur-electricite'}`,
      color: 'from-yellow-500 to-yellow-600',
      badge: 'Fournisseurs'
    },
    { 
      key: 'ebooks', 
      icon: BookOpen, 
      path: `${langPrefix}/ebooks`,
      color: 'from-emerald-500 to-emerald-600',
      badge: '5-10 guides'
    }
  ];

  const features = [
    {
      key: 'inflation',
      icon: Shield,
      color: 'from-red-500 to-red-600'
    },
    {
      key: 'power',
      icon: TrendingUp,
      color: 'from-green-500 to-green-600'
    },
    {
      key: 'ecology',
      icon: Leaf,
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      key: 'efficiency',
      icon: Clock,
      color: 'from-blue-500 to-blue-600'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead type="homepage" />
      <StructuredData type="home" />

      {/* Popup de rétention - affiché quand l'utilisateur vient d'une page fournisseur */}
      {showRetentionPopup && providerCountry && providerVertical && (
        <RetentionPopup
          country={providerCountry}
          vertical={providerVertical}
          onClose={() => setShowRetentionPopup(false)}
        />
      )}
      
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background with gradient and subtle pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-sage-50 via-white to-sage-100">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KPGcgZmlsbD0iIzAwNzY1ZSIgZmlsbC1vcGFjaXR5PSIwLjAzIj4KPGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iNCIvPgo8L2c+CjwvZz4KPC9zdmc+')] opacity-40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="text-center lg:text-left">
              <div className="mb-6">
                <Badge className="bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-900 border-amber-200 mb-6 px-4 py-2 text-sm font-semibold">
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('hero.badge')}
                </Badge>
                <h1 
                  className="text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-sage-800 via-sage-900 to-sage-800 mb-6 leading-tight"
                  data-testid="hero-title"
                  style={{
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {isSpanish ? 
                    <>Recupera el control de tus <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">gastos</span> y mejora tu <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">economía</span></> :
                    <>Reprenez le contrôle de votre <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-600">pouvoir d'achat</span></>
                  }
                </h1>
                <p 
                  className="text-lg md:text-xl lg:text-2xl text-sage-700 mb-8 max-w-2xl mx-auto lg:mx-0 font-medium leading-relaxed"
                  data-testid="hero-subtitle"
                >
                  {isSpanish ?
                    <>Savea, la <span className="text-amber-600 font-semibold">plataforma todo-en-uno</span> para combatir la inflación y ganar poder adquisitivo.</> :
                    <>Savea, la <span className="text-amber-600 font-semibold">plateforme tout-en-un</span> pour lutter contre l'inflation et gagner en pouvoir d'achat.</>
                  }
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  asChild 
                  size="lg"
                  className="bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-700 hover:to-sage-800 text-white px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
                  data-testid="hero-primary-cta"
                >
                  <Link to={`${langPrefix}/${isSpanish ? 'herramientas' : 'outils'}`} className="flex items-center gap-2">
                    {t('hero.cta')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <a 
                  href={`tel:${getSaveaPhoneTel(country, 'electricity', 'LP')}`}
                  className="inline-flex items-center justify-center border-2 border-sage-700 text-sage-800 bg-white hover:bg-sage-700 hover:text-white px-8 py-4 rounded-full transition-all duration-300 font-semibold"
                  data-testid="hero-secondary-cta"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {t('hero.cta_secondary')}
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mt-12 pt-8 border-t border-sage-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-sage-800">9+</div>
                  <div className="text-sm text-sage-600">{t('hero.stats.tools')}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-sage-800">16+</div>
                  <div className="text-sm text-sage-600">{isSpanish ? 'Plantillas emails' : 'Modèles emails'}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-sage-800">2</div>
                  <div className="text-sm text-sage-600">{isSpanish ? 'Países' : 'Pays'}</div>
                </div>
              </div>
            </div>

            {/* Hero Images Grid */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4 h-[500px]">
                {heroImages.slice(0, 4).map((image, index) => (
                  <div 
                    key={index}
                    className={`relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                      index === 0 ? 'row-span-2' : ''
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`SAVEA illustration ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                ))}
              </div>
              
              {/* Floating element */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-sage-400 to-sage-500 rounded-full opacity-20 animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Guide Anti-Inflation - Mise en avant forte - SEULEMENT FRANCE */}
      {!isSpanish && (
        <section className="py-20 bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 relative overflow-hidden">
          {/* Éléments décoratifs */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/10 rounded-full translate-x-24 translate-y-24"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <Badge className="bg-white/20 text-white border-white/30 px-4 py-2 text-lg">
                  🎉 ENFIN DISPONIBLE
                </Badge>
              </div>

              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                🏆 Notre Guide Anti-Inflation 2026<br />
                <span className="text-white/90">100% Gratuit !</span>
              </h2>
              
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                <strong className="text-white">Découvrez comment économiser entre 1 500 € et 3 000 € par an</strong> avec nos 9 chapitres détaillés. 
                Conseils testés et validés par les experts SAVEA, sans vous priver de votre confort.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  asChild
                  size="lg"
                  className="bg-white text-amber-600 hover:bg-amber-50 font-bold text-xl px-8 py-4 shadow-2xl hover:shadow-3xl transition-all duration-300"
                >
                  <Link to={`${langPrefix}/guide-anti-inflation`}>
                    <BookOpen className="w-6 h-6 mr-3" />
                    Consulter le Guide Gratuit
                  </Link>
                </Button>
                
                <a 
                  href={`tel:${getSaveaPhoneTel(country, 'electricity', 'LP')}`}
                  className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-amber-600 font-bold text-xl px-8 py-4 rounded-full"
                >
                  <Phone className="w-6 h-6 mr-3" />
                  Parler à un Expert
                </a>
              </div>

              {/* Statistiques impressionnantes */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-white">9</div>
                  <div className="text-white/80 text-sm">Chapitres détaillés</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-white">3000€</div>
                  <div className="text-white/80 text-sm">Économies max/an</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-white">30+</div>
                  <div className="text-white/80 text-sm">Liens utiles</div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-white">100%</div>
                  <div className="text-white/80 text-sm">Gratuit</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tools Grid Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-sage-900 mb-4">
              {t('tools.title')}
            </h2>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {t('tools.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => (
              <Card 
                key={tool.key} 
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-md overflow-hidden"
                data-testid={`tool-card-${tool.key}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <tool.icon className="w-6 h-6 text-white" />
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {tool.badge}
                    </Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-sage-900 mb-2">
                    {t(`tools.${tool.key}.title`)}
                  </h3>
                  <p className="text-sage-600 text-sm mb-4 leading-relaxed">
                    {t(`tools.${tool.key}.description`)}
                  </p>
                  
                  <Button 
                    asChild 
                    variant="ghost" 
                    className="w-full justify-between group-hover:bg-sage-50 transition-colors"
                  >
                    <Link to={tool.path}>
                      Découvrir
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}

            {/* Carte spéciale Guide Anti-Inflation - SEULEMENT FRANCE */}
            {!isSpanish && (
              <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-amber-200 shadow-md overflow-hidden bg-gradient-to-br from-amber-50 to-yellow-50">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                      NOUVEAU 2026
                    </Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-sage-900 mb-2">
                    🏆 Guide Anti-Inflation 2026
                  </h3>
                  <p className="text-sage-600 text-sm mb-4 leading-relaxed">
                    Guide complet gratuit avec conseils testés et validés par SAVEA. 
                    9 chapitres pour optimiser vos dépenses.
                  </p>
                  
                  <Button 
                    asChild 
                    className="w-full justify-between bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
                  >
                    <Link to={`${langPrefix}/guide-anti-inflation`}>
                      Consulter le Guide Gratuit
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-sage-50 to-sage-100" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-sage-900 mb-4">
              {t('features.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div 
                key={feature.key} 
                className="text-center group"
                data-testid={`feature-${feature.key}`}
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-sage-900 mb-2">
                  {t(`features.${feature.key}.title`)}
                </h3>
                <p className="text-sage-600 leading-relaxed">
                  {t(`features.${feature.key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-3xl p-8 lg:p-12 shadow-2xl">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {t('actions.ready_control')}
            </h2>
            <p className="text-sage-100 text-lg mb-8 max-w-2xl mx-auto">
              {t('actions.join_thousands')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg"
                className="bg-white text-sage-700 hover:bg-sage-50 px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="cta-start-now"
              >
                <Link to={`${langPrefix}/${isSpanish ? 'herramientas' : 'outils'}`}>{t('actions.start_now')}</Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-sage-700 px-8 py-4 rounded-full transition-all duration-300"
                data-testid="cta-learn-more"
              >
                <Link to={`${langPrefix}/${isSpanish ? 'acerca-de' : 'a-propos'}`}>{t('actions.learn_more')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};